import React from 'react';
import PropTypes, { any } from 'prop-types';
import { get, kebabCase } from 'lodash';
import { CtaCard, StatCard } from 'components/molecules';
import { black, white } from 'styles/colors';
import { ThemeProvider } from 'styled-components';
import { Headline } from 'components/atoms';
import * as Styled from './Gallery.styled';

/**
 * Renders the Gallery
 * @param {number} columns
 * @param {number} rows
 * @param {Array} items
 * @param {boolean} fullWidth full width style
 * @param {string} title title
 * @param {string} backgroundColor background color
 * @param {string} textColor text color
 * @param {string} caption caption
 * @returns {ReactElement}
 */
const Gallery = ({
  columns = 1,
  rows = 1,
  items = [],
  fullWidth = false,
  backgroundColor = black,
  textColor = white,
  title = '',
  caption = '',
}) => {
  const renderItem = (item, index) => {
    // eslint-disable-next-line no-underscore-dangle
    switch (item.__typename) {
      case 'CtaCard':
        return (
          <CtaCard
            key={kebabCase(`${item.title}-${index}`)}
            title={item.title}
            description={item.description}
            image={item.image}
            ctas={get(item, 'ctasCollection.items', [])}
            themeSelector={item.themeSelector}
            className="gallery-card"
          />
        );
      case 'ModuleImage':
        return (
          <Styled.ImageContainer
            key={kebabCase(`image-${index}`)}
            src={item.image.url}
            alt={item.image.description}
          />
        );
      case 'StatCard':
        return <StatCard key={kebabCase(`${item.title}-${index}`)} {...item} />;
      default:
        return <></>;
    }
  };

  return (
    <ThemeProvider theme={{ backgroundColor, textColor }}>
      <Styled.Gallery className="gallery-module">
        {title && (
          <Styled.HeadlineContainer>
            {title && (
              <Styled.Title>
                <Headline tag="h1" styling="h1" text={title} />
              </Styled.Title>
            )}
            {caption && <Styled.Caption>{caption}</Styled.Caption>}
          </Styled.HeadlineContainer>
        )}
        <Styled.GridContainer
          fullWidth={fullWidth}
          columns={columns}
          rows={rows}
          className="gallery-grid"
        >
          {items.map((item, index) => renderItem(item, index))}
        </Styled.GridContainer>
      </Styled.Gallery>
    </ThemeProvider>
  );
};

Gallery.propTypes = {
  /**
   * The number of columns in the gallery
   */
  columns: PropTypes.string,
  /**
   * The number of rows in the gallery
   */
  rows: PropTypes.string,
  /**
   * The title of the gallery
   */
  title: PropTypes.string,
  /**
   * List of gallery items
   */
  items: PropTypes.arrayOf(any),
  /**
   * full width styling
   */
  fullWidth: PropTypes.bool,
  /**
   * caption
   */
  caption: PropTypes.string,
};

export default Gallery;
